import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n";
import { track } from "../lib/analytics";
import "./MotionHero.css";

/**
 * YUXEL / Approved Editorial Floating Cards, version 4
 * This component is a drop-in replacement for MotionHero, not a page replacement.
 * The images below are cropped from the approved YUXEL art direction.
 * It does not change Home.jsx, its service content, client logos, or other routes.
 */
const COPY = {
  az: {
    headlineTop: "İdeyadan",
    headlineBottom: "təsirə.",
    eyebrow: "YUXEL / BUSINESS TRANSFORMATION",
    work: "İşlərimiz",
    contact: "Əlaqə",
    scroll: "Kəşf etmək üçün sürüşdür",
    visual: "Kreativ konsept",
  },
  en: {
    headlineTop: "From ideas",
    headlineBottom: "to impact.",
    eyebrow: "YUXEL / BUSINESS TRANSFORMATION",
    work: "Our work",
    contact: "Contact",
    scroll: "Scroll to explore",
    visual: "Creative concept",
  },
};

// The six cards follow the composition approved in chat (apple, doorway,
// textile, red liquid, equilibrium, red glass). Their independent scroll paths
// create the feeling of objects floating in space, not a single static image.
const CARDS = [
  { id: 1, x: 6.9, y: 8.7, width: 20.5, height: 44, angle: -9, depth: 3, dx: -260, dy: -360, dr: -24, scale: 1.07 },
  { id: 2, x: 33.0, y: 0.6, width: 15.8, height: 31.5, angle: -3, depth: 2, dx: -95, dy: -510, dr: 12, scale: 1.15 },
  { id: 3, x: 69.3, y: 7.8, width: 21.1, height: 43, angle: 8, depth: 5, dx: 270, dy: -430, dr: 27, scale: 1.1 },
  { id: 4, x: 11.6, y: 53.0, width: 24.5, height: 40, angle: -8, depth: 4, dx: -345, dy: -260, dr: -23, scale: 1.14 },
  { id: 5, x: 48.6, y: 56.2, width: 18.5, height: 39, angle: 5, depth: 6, dx: 75, dy: -570, dr: 20, scale: 1.09 },
  { id: 6, x: 77.2, y: 50.6, width: 17.4, height: 41, angle: 8, depth: 3, dx: 325, dy: -315, dr: 17, scale: 1.18 },
];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function FloatingCard({ card, progress, reducedMotion, selected, onSelect, visualLabel }) {
  // Hooks are deliberately called in a dedicated child, not in a .map loop.
  const sx = useTransform(progress, [0, 0.15, 0.80, 1], [0, 0, reducedMotion ? 0 : card.dx, reducedMotion ? 0 : card.dx * 1.15]);
  const sy = useTransform(progress, [0, 0.15, 0.80, 1], [0, 0, reducedMotion ? 0 : card.dy, reducedMotion ? 0 : card.dy * 1.1]);
  const sr = useTransform(progress, [0, 0.15, 0.8, 1], [card.angle, card.angle, reducedMotion ? card.angle : card.angle + card.dr, reducedMotion ? card.angle : card.angle + card.dr * 1.08]);
  const ss = useTransform(progress, [0, 0.4, 0.85, 1], [1, 1, reducedMotion ? 1 : card.scale, reducedMotion ? 1 : card.scale * 1.03]);
  const so = useTransform(progress, [0, 0.66, 0.94, 1], [1, 1, 0.5, 0]);
  const x = sx;
  const y = sy;
  const rotate = sr;

  return (
    <motion.button
      type="button"
      className={`ym4-card ym4-card-${card.id}`}
      data-testid={`hero-floating-card-${card.id}`}
      aria-label={`${visualLabel} ${card.id}`}
      aria-pressed={selected}
      onClick={() => onSelect(card.id)}
      style={{
        "--ym4-card-index": card.id,
        left: `${card.x}%`, top: `${card.y}%`,
        width: `${card.width}%`, height: `${card.height}%`,
        zIndex: selected ? 12 : card.depth, x, y, rotate, scale: ss, opacity: so,
      }}
    >
      <span className="ym4-card-float" data-selected={selected} style={{ animationDelay: `${-card.id * 0.73}s`, animationDuration: `${5.3 + card.id * 0.55}s` }}>
        <img src={`/motion-concepts/card-${card.id}.webp`} alt="" loading={card.id <= 3 ? "eager" : "lazy"} decoding="async" />
        <span className="ym4-card-reflection" aria-hidden="true" />
      </span>
    </motion.button>
  );
}

export default function MotionHero() {
  const { lang, href } = useLang();
  const copy = COPY[lang] || COPY.en;
  const section = useRef(null);
  const scene = useRef(null);
  const [selected, setSelected] = useState(null);
  const reducedMotion = Boolean(useReducedMotion());
  const { scrollYProgress } = useScroll({ target: section, offset: ["start start", "end end"] });
  const headlineY = useTransform(scrollYProgress, [0, 0.55, 1], [0, reducedMotion ? 0 : -28, reducedMotion ? 0 : -110]);
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.13, 0.50], [1, 1, 0]);
  const shadeOpacity = useTransform(scrollYProgress, [0.68, 1], [0, 1]);
  const move = (event) => {
    if (reducedMotion || !scene.current || event.pointerType === "touch") return;
    const box = scene.current.getBoundingClientRect();
    const px = clamp((event.clientX - box.left) / box.width - 0.5, -0.5, 0.5);
    const py = clamp((event.clientY - box.top) / box.height - 0.5, -0.5, 0.5);
    scene.current.style.setProperty("--ym4-mx", (px * 2).toFixed(3));
    scene.current.style.setProperty("--ym4-my", (py * 2).toFixed(3));
  };
  const reset = () => {
    if (!scene.current) return;
    scene.current.style.setProperty("--ym4-mx", "0");
    scene.current.style.setProperty("--ym4-my", "0");
  };
  const onSelect = (id) => {
    setSelected((current) => (current === id ? null : id));
    track("hero_card_interaction", { card: id, component: "editorial_floating_cards" });
  };

  return (
    <section className="yuxel-motion-hero ym4-hero" ref={section} data-testid="motion-hero" aria-label={`${copy.headlineTop} ${copy.headlineBottom}`}>
      <div className="ym4-sticky">
        
        <div className="ym4-vignette" aria-hidden="true" />
        <div className="ym4-scene" ref={scene} onPointerMove={move} onPointerLeave={reset}>
          <div className="ym4-cards">
            {CARDS.map((card) => (
              <FloatingCard key={card.id} card={card} progress={scrollYProgress} reducedMotion={reducedMotion} selected={selected === card.id} onSelect={onSelect} visualLabel={copy.visual} />
            ))}
          </div>
        </div>
        <div className="ym4-head-anchor">
          <motion.div className="ym4-head" style={{ y: headlineY, opacity: headlineOpacity }}>
            <span className="ym4-eyebrow">{copy.eyebrow}</span>
            <h1><span>{copy.headlineTop}</span><span className="ym4-second">{copy.headlineBottom}</span></h1>
          </motion.div>
        </div>
        <motion.div className="ym4-exit-shade" style={{ opacity: shadeOpacity }} aria-hidden="true" />
      </div>
    </section>
  );
}
